// ---------------------------------------------------------------------------
// Blob-table sync helpers. attempt_audio / attempt_video / profile_document hold
// large binaries that must NOT go into SQL rows on the server, so each syncs as:
//   1. the bytes  -> POST /api/media/upload      (Azure Blob Storage)
//   2. a meta doc -> "<table>_meta" sync document (the non-blob columns)
// On the receiving device the meta doc recreates the row (with an empty blob
// placeholder) and the downloaded bytes fill in the data column.
// ---------------------------------------------------------------------------

import { syncDb } from './local';
import type { SqlValue } from 'sql.js';

type BlobRow = Record<string, SqlValue>;

function queryOne(sql: string, params: SqlValue[] = []): BlobRow | null {
  const stmt = syncDb().prepare(sql);
  try {
    stmt.bind(params);
    return stmt.step() ? stmt.getAsObject() : null;
  } finally {
    stmt.free();
  }
}
function getDb() {
  return syncDb();
}

interface BlobTableInfo {
  /** Columns other than the binary `data` column, in insert order. */
  metaCols: string[];
  /** Build the WHERE clause + params from an outbox entity id. */
  where: (entityId: string) => { clause: string; params: SqlValue[]; pk: string[]; pkValues: SqlValue[] };
}

const BLOB_TABLES: Record<string, BlobTableInfo> = {
  attempt_audio: {
    metaCols: ['attempt_id', 'mime', 'created_at'],
    where: (id) => ({ clause: 'attempt_id = ?', params: [id], pk: ['attempt_id'], pkValues: [id] }),
  },
  attempt_video: {
    metaCols: ['attempt_id', 'mime', 'self_review', 'created_at'],
    where: (id) => ({ clause: 'attempt_id = ?', params: [id], pk: ['attempt_id'], pkValues: [id] }),
  },
  profile_document: {
    // 'text' (the extracted application text) is deliberately NOT synced: a full
    // AADSAS extract can exceed the server's 1 MB doc-payload cap and fail the
    // meta push after the bytes uploaded fine. Receiving devices re-extract it
    // from the downloaded bytes (see sync/postApply.ts).
    metaCols: ['profile_id', 'kind', 'filename', 'mime', 'created_at'],
    where: (id) => {
      const [profileId, kind] = id.split('|');
      return {
        clause: 'profile_id = ? AND kind = ?',
        params: [profileId, kind],
        pk: ['profile_id', 'kind'],
        pkValues: [profileId, kind],
      };
    },
  },
};

export function metaEntityType(blobType: string): string {
  return `${blobType}_meta`;
}
export function blobTypeFromMeta(metaType: string): string | null {
  return metaType.endsWith('_meta') ? metaType.slice(0, -'_meta'.length) : null;
}

export interface LocalBlob {
  bytes: Uint8Array;
  mime: string;
  /** JSON of the non-blob columns (the meta-doc payload). */
  metaPayload: string;
}

/** Read a blob row: its bytes, mime, and meta payload. Null if the row is gone. */
export function readBlob(type: string, entityId: string): LocalBlob | null {
  const info = BLOB_TABLES[type];
  if (!info) return null;
  const { clause, params } = info.where(entityId);
  const row = queryOne(`SELECT * FROM ${type} WHERE ${clause}`, params);
  if (!row) return null;
  const meta: Record<string, unknown> = {};
  for (const col of info.metaCols) meta[col] = (row as Record<string, SqlValue>)[col] ?? null;
  const data = (row as Record<string, SqlValue>).data;
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(0);
  return { bytes, mime: String((row as Record<string, SqlValue>).mime ?? 'application/octet-stream'), metaPayload: JSON.stringify(meta) };
}

/** Recreate/refresh a blob row's non-blob columns from a meta-doc payload. */
export function applyBlobMeta(blobType: string, metaPayload: string): void {
  const info = BLOB_TABLES[blobType];
  if (!info) return;
  let obj: Record<string, unknown>;
  try {
    obj = JSON.parse(metaPayload);
  } catch {
    return;
  }
  const cols = info.metaCols.filter((c) => obj[c] !== undefined);
  if (cols.length === 0) return;
  const allCols = [...cols, 'data'];
  const placeholders = allCols.map(() => '?').join(', ');
  // Keep existing bytes if the row already exists; only default to empty on insert.
  const updates = cols.map((c) => `${c} = excluded.${c}`).join(', ');
  const values: SqlValue[] = [...cols.map((c) => obj[c] as SqlValue), new Uint8Array(0)];
  const sql =
    `INSERT INTO ${blobType} (${allCols.join(', ')}) VALUES (${placeholders}) ` +
    `ON CONFLICT(${info.where('x').pk.join(', ')}) DO UPDATE SET ${updates}`;
  getDb().run(sql, values);
}

/** True when the local row a blob's bytes belong to already exists. */
export function blobRowExists(blobType: string, entityId: string): boolean {
  const info = BLOB_TABLES[blobType];
  if (!info) return false;
  const { clause, params } = info.where(entityId);
  return queryOne(`SELECT 1 AS x FROM ${blobType} WHERE ${clause}`, params) !== null;
}

/**
 * Fill in a blob row's bytes after downloading them. Returns false when the
 * row doesn't exist yet (its meta doc hasn't arrived) so the caller can queue
 * the download for retry instead of losing the bytes.
 */
export function applyBlobBytes(blobType: string, entityId: string, bytes: Uint8Array, mime: string): boolean {
  const info = BLOB_TABLES[blobType];
  if (!info) return false;
  const { clause, params } = info.where(entityId);
  if (!blobRowExists(blobType, entityId)) return false;
  getDb().run(`UPDATE ${blobType} SET data = ?, mime = ? WHERE ${clause}`, [bytes, mime, ...params]);
  return true;
}

// --- pending blob downloads --------------------------------------------------
// A pulled blob change references bytes in cloud storage, but the local row it
// fills lives in a separate meta DOCUMENT that can arrive in a later pull page
// (or a later sync run) because blob uploads get their own server revision.
// Rather than dropping the bytes (the cursor has already moved past them),
// remember the server blob id and retry after every pull.

export interface PendingBlob {
  entityType: string;
  entityId: string;
  blobId: string;
}

export function setPendingBlob(blobType: string, entityId: string, blobId: string): void {
  getDb().run(
    `INSERT INTO sync_pending_blob (entity_type, entity_id, blob_id) VALUES (?, ?, ?)
     ON CONFLICT(entity_type, entity_id) DO UPDATE SET blob_id = excluded.blob_id`,
    [blobType, entityId, blobId],
  );
}

export function clearPendingBlob(blobType: string, entityId: string): void {
  getDb().run('DELETE FROM sync_pending_blob WHERE entity_type = ? AND entity_id = ?', [blobType, entityId]);
}

export function listPendingBlobs(): PendingBlob[] {
  const stmt = getDb().prepare('SELECT entity_type, entity_id, blob_id FROM sync_pending_blob');
  const rows: PendingBlob[] = [];
  try {
    while (stmt.step()) {
      const r = stmt.getAsObject();
      rows.push({ entityType: String(r.entity_type), entityId: String(r.entity_id), blobId: String(r.blob_id) });
    }
  } finally {
    stmt.free();
  }
  return rows;
}

export function deleteBlobRow(blobType: string, entityId: string): void {
  const info = BLOB_TABLES[blobType];
  if (!info) return;
  const { clause, params } = info.where(entityId);
  getDb().run(`DELETE FROM ${blobType} WHERE ${clause}`, params);
}
